import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, IconButton,
  ToggleButtonGroup, ToggleButton, Alert, Snackbar, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useIsMobile from '../hooks/useIsMobile';
import { messageErreurs } from '../services/formulairesServices';

// Composeur de mail GÉNÉRIQUE (lot 5 du plan « constructeur de formulaires », conçu pour servir ensuite aux 7 mails
// admin) : destinataire, objet et corps HTML pré-remplis par le parent, modifiables, aperçu à droite, puis « Envoyer ».
// Le composant ne connaît ni les templates ni l'API : l'envoi est délégué à `onEnvoyer`, qui renvoie le résultat
// ({ envoye, indetermine?, erreur? }) ou rejette (erreur axios 400/409/502 → messageErreurs). En erreur le dialog
// reste ouvert, contenu intact ; après succès le bouton « Envoyer » devient « Fermer ».
// Deux modes d'édition du corps : « HTML brut » (défaut, TextField monospace, conserve les styles inline du bouton
// des templates) et « Visuel » (ReactQuill, monté seulement quand l'onglet est actif). react-quill 2.0.0 rappelle
// onChange dès le montage avec le HTML normalisé (lib/index.js : setEditorContents → onEditorChangeText, source 'api'),
// ce qui dégraderait le bouton stylé sans aucune saisie : seules les modifications de source 'user' sont retenues.

// Résultat attendu de `onEnvoyer` (forme de `mail` dans le 200 / 502 de POST /formulaires/dossiers/:id/envoyer)
export type ResultatComposeur = { envoye: boolean; indetermine?: boolean; erreur?: string };

export interface ComposerMailDialogProps {
  open: boolean;                 // Dialog ouvert ; les états sont ré-initialisés depuis les props à chaque ouverture
  titre: string;                 // titre du Dialog (« Envoyer le formulaire au client »…)
  destinataire: string;          // e-mail pré-rempli (modifiable)
  sujet: string;                 // objet pré-rempli
  corpsHtml: string;             // corps HTML pré-rempli (rendu du template côté serveur)
  chargement?: boolean;          // true : CircularProgress à la place du contenu (aperçu en cours de préparation)
  avertissement?: string;        // texte affiché en Alert warning en haut du contenu (ex. dossier archivé)
  onEnvoyer: (p: { email: string; sujet: string; corps_html: string }) => Promise<ResultatComposeur>;
  onClose: () => void;           // Annuler, croix, ou « Fermer » après succès
}

type Mode = 'html' | 'visuel';

interface Resultat {
  severity: 'success' | 'warning' | 'error';
  message: string;
}

// Regex e-mail partagée avec les backends (§11 n°5 du plan)
const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Message de l'Alert affichée au-dessus de l'éditeur visuel quand le corps porte des styles inline
const AVERTISSEMENT_VISUEL =
  'L\'édition visuelle simplifie la mise en page (styles du bouton perdus) — préférez « HTML brut » pour modifier le texte';

const ComposerMailDialog: React.FC<ComposerMailDialogProps> = ({
  open, titre, destinataire, sujet: sujetInitial, corpsHtml, chargement = false, avertissement, onEnvoyer, onClose,
}) => {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [mode, setMode] = useState<Mode>('html');
  const [envoi, setEnvoi] = useState(false);       // envoi en cours
  const [envoye, setEnvoye] = useState(false);     // succès : le bouton devient « Fermer »
  const [resultat, setResultat] = useState<Resultat | null>(null); // Alert persistante dans le dialog
  const [toast, setToast] = useState<Resultat | null>(null);       // Snackbar (même message, transitoire)

  // Dernières props reçues, lues par l'effet d'ouverture sans figurer dans ses dépendances : les états ne sont
  // ré-initialisés qu'à l'ouverture (et à la fin du chargement), jamais à un simple re-rendu du parent.
  const initial = useRef({ destinataire, sujet: sujetInitial, corpsHtml });
  useLayoutEffect(() => {
    initial.current = { destinataire, sujet: sujetInitial, corpsHtml };
  });

  // À chaque ouverture : état d'envoi remis à zéro (même pendant un chargement, pour ne pas montrer le résultat
  // d'une session précédente derrière le spinner) et retour sur « HTML brut »
  useEffect(() => {
    if (!open) return;
    setMode('html');
    setEnvoi(false);
    setEnvoye(false);
    setResultat(null);
    setToast(null);
  }, [open]);

  // Champs pré-remplis depuis les props, à l'ouverture ou à la fin du chargement
  useEffect(() => {
    if (!open || chargement) return;
    setEmail(initial.current.destinataire || '');
    setSujet(initial.current.sujet || '');
    setCorps(initial.current.corpsHtml || '');
  }, [open, chargement]);

  const emailValide = REGEX_EMAIL.test(email.trim());
  const peutEnvoyer = emailValide && sujet.trim() !== '' && corps.trim() !== '' && !envoi && !chargement;
  const verrouille = envoi || envoye; // champs figés pendant l'envoi et après succès

  // Résultat d'un envoi : Alert persistante dans le dialog + Snackbar
  const signaler = (r: Resultat) => { setResultat(r); setToast(r); };

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const r = await onEnvoyer({ email: email.trim(), sujet, corps_html: corps });
      signaler(r.envoye
        ? { severity: r.indetermine ? 'warning' : 'success', message: r.indetermine ? 'Envoi non confirmé (délai Brevo) : considéré comme envoyé, vérifiez le journal' : 'Mail envoyé' }
        : { severity: 'error', message: r.erreur || 'Envoi impossible' });
      if (r.envoye) setEnvoye(true);
    } catch (e) {
      signaler({ severity: 'error', message: messageErreurs(e, 'Envoi impossible') });
    } finally { setEnvoi(false); }
  };

  // Pas de fermeture (fond, Échap, croix, Annuler) pendant l'envoi
  const fermer = () => { if (!envoi) onClose(); };

  const editeur = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: isMobile ? 'visible' : 'auto', pr: isMobile ? 0 : 1 }}>
      <TextField
        id="composeur-destinataire"
        label="Destinataire"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={email.trim() !== '' && !emailValide}
        helperText={email.trim() !== '' && !emailValide ? 'Adresse e-mail invalide' : undefined}
        disabled={verrouille}
        fullWidth
        size="small"
        sx={{ mb: 1.5, mt: 0.5 }}
      />
      <TextField
        id="composeur-objet"
        label="Objet"
        value={sujet}
        onChange={(e) => setSujet(e.target.value)}
        disabled={verrouille}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
        <Typography variant="subtitle2">Corps du message</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_e, v: Mode | null) => { if (v) setMode(v); }}
          disabled={verrouille}
        >
          <ToggleButton value="html"><CodeIcon fontSize="small" sx={{ mr: 0.5 }} />HTML brut</ToggleButton>
          <ToggleButton value="visuel"><VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />Visuel</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'visuel' ? (
        <Box sx={{ '& .ql-container': { minHeight: 260 }, '& .ql-editor': { minHeight: 260 }, mb: 1 }}>
          {/style=/i.test(corps) && (
            <Alert severity="warning" sx={{ mb: 1 }}>{AVERTISSEMENT_VISUEL}</Alert>
          )}
          {/* Monté seulement dans ce mode ; onChange de source 'api' (montage, normalisation) ignoré */}
          <ReactQuill
            theme="snow"
            value={corps}
            readOnly={verrouille}
            onChange={(html, _delta, source) => { if (source === 'user') setCorps(html); }}
          />
        </Box>
      ) : (
        <TextField
          id="composeur-corps"
          label="Corps du message (HTML)"
          value={corps}
          onChange={(e) => setCorps(e.target.value)}
          disabled={verrouille}
          fullWidth
          multiline
          minRows={14}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          sx={{ mb: 1 }}
        />
      )}
    </Box>
  );

  const apercu = (
    <Box
      sx={{
        flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
        borderLeft: isMobile ? 0 : 1, borderTop: isMobile ? 1 : 0, borderColor: 'divider',
        pl: isMobile ? 0 : 2, pt: isMobile ? 2 : 0, minHeight: isMobile ? 420 : 0,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Aperçu</Typography>
      <Box sx={{ px: 1, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">Objet</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{sujet || '—'}</Typography>
      </Box>
      <Box sx={{ flexGrow: 1, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minHeight: isMobile ? 320 : 0 }}>
        {/* sandbox vide : rien ne s'exécute dans l'aperçu (ni script, ni formulaire, ni navigation) */}
        <iframe
          title="apercu"
          srcDoc={corps}
          sandbox=""
          style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={fermer}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { height: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {titre}
          </Box>
          <IconButton onClick={fermer} disabled={envoi} aria-label="Fermer la fenêtre" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {chargement ? (
          <DialogContent dividers sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </DialogContent>
        ) : (
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: isMobile ? 'auto' : 'hidden' }}>
            {avertissement && <Alert severity="warning">{avertissement}</Alert>}
            {resultat && (
              <Alert severity={resultat.severity} onClose={() => setResultat(null)}>{resultat.message}</Alert>
            )}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, minHeight: 0 }}>
              {editeur}
              {apercu}
            </Box>
          </DialogContent>
        )}

        <DialogActions>
          {envoye ? (
            <Button variant="contained" onClick={onClose}>Fermer</Button>
          ) : (
            <>
              <Button onClick={fermer} disabled={envoi}>Annuler</Button>
              <Button
                variant="contained"
                onClick={envoyer}
                disabled={!peutEnvoyer}
                startIcon={envoi ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              >
                {envoi ? 'Envoi…' : 'Envoyer'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={(_e, raison) => { if (raison !== 'clickaway') setToast(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default ComposerMailDialog;
